import { DateTime } from 'luxon';
import type { Locale } from '@saniplus/contracts';

/**
 * Every timestamp in the system is UTC (D-13) and must render in its **site's**
 * timezone, with the zone abbreviation wherever ambiguity is possible —
 * overnight shifts and exports (01-PRODUCT-SPEC §Timezone display).
 *
 * Date maths goes through luxon and the site zone, never fixed-millisecond
 * arithmetic: a March or November shift is 23 or 25 hours long.
 */

/** The API locale codes map to the Canadian variants for formatting. */
const INTL_LOCALE: Record<Locale, string> = { fr: 'fr-CA', en: 'en-CA' };

export function intlLocale(locale: Locale): string {
  return INTL_LOCALE[locale];
}

/**
 * Parse a UTC instant and convert it into the site's zone.
 *
 * Deliberately *not* `setZone: true`: that option tells luxon to keep the
 * offset written in the string, which for our `Z`-suffixed instants means
 * "stay in UTC" — every timestamp would render in the wrong zone while
 * looking perfectly plausible.
 */
export type Instant = string | Date;

function zoned(value: Instant, zone: string, locale: Locale): DateTime {
  const dt =
    typeof value === 'string'
      ? DateTime.fromISO(value, { zone })
      : DateTime.fromJSDate(value, { zone });
  return dt.setLocale(intlLocale(locale));
}

/**
 * `form: 'grid'` (the default) keeps `HH:mm` in every locale — a schedule
 * column or a tabular ledger where French and English rows must line up on
 * the same colon, and where the extra half-width glyphs of `h` would widen
 * only the French column.
 *
 * `form: 'prose'` is for a sentence or a standalone readout — e.g. the
 * worker rail clock: French gets its own spoken form, `21 h 12` (U+202F
 * NARROW NO-BREAK SPACE around the `h`, never a plain space — it must not
 * linewrap between the number and the letter). English is unaffected,
 * `21:12` either way.
 */
export interface FormatZonedTimeOptions {
  readonly form?: 'grid' | 'prose';
}

/**
 * NARROW NO-BREAK SPACE (U+202F).
 *
 * `fr-CA` typography sets `21 h 12` and `28 h 35` with this, never a plain
 * space: a plain space lets a line break fall between the number and the `h`,
 * which is how one of the copies this module replaced wrapped a payroll
 * duration in half. Exported so a caller assembling its own sentence uses the
 * same glyph.
 */
export const NARROW_NBSP = ' ';

/** `14:05` (en) / `14 h 05` (fr prose) — 24h, the only clock a night crew reads. */
export function formatZonedTime(
  value: Instant,
  zone: string,
  locale: Locale,
  options?: FormatZonedTimeOptions,
): string {
  const dt = zoned(value, zone, locale);
  const form = options?.form ?? 'grid';
  if (locale === 'fr' && form === 'prose') {
    return `${dt.toFormat('HH')}${NARROW_NBSP}h${NARROW_NBSP}${dt.toFormat('mm')}`;
  }
  return dt.toFormat('HH:mm');
}

/** `2026-03-08` in the site's zone. */
export function formatZonedDate(value: Instant, zone: string, locale: Locale): string {
  return zoned(value, zone, locale).toFormat('yyyy-LL-dd');
}

/** `sam. 7 mars` / `Sat, Mar 7` — a list header, not a timestamp. */
export function formatZonedDayLabel(value: Instant, zone: string, locale: Locale): string {
  return zoned(value, zone, locale).toLocaleString({
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/** `EST` / `EDT` (`HNE` / `HAE` in French) for the instant, not for "now". */
export function zoneAbbreviation(value: Instant, zone: string, locale: Locale): string {
  return zoned(value, zone, locale).toFormat('ZZZZ');
}

/** `2026-03-08 01:30 EST` — the disambiguated form for overnight and exports. */
export function formatZonedDateTime(value: Instant, zone: string, locale: Locale): string {
  const dt = zoned(value, zone, locale);
  return `${dt.toFormat('yyyy-LL-dd HH:mm')} ${dt.toFormat('ZZZZ')}`;
}

/** `22:00 EDT` — a time that could be read in the wrong zone. */
export function formatZonedTimeWithZone(value: Instant, zone: string, locale: Locale): string {
  const dt = zoned(value, zone, locale);
  return `${dt.toFormat('HH:mm')} ${dt.toFormat('ZZZZ')}`;
}

/** `27 août 2026` / `August 27, 2026` — the long date, for prose and dialog titles. */
export function formatZonedDateProse(value: Instant, zone: string, locale: Locale): string {
  return zoned(value, zone, locale).toLocaleString({
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/* -------------------------------------------------------------------------- */
/* Durations                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * The two forms a duration is allowed to take, and nothing else.
 *
 * - **`grid`** — `7:30`, `28:35`. A timesheet column or a ledger, where the
 *   rows line up on the colon and the hours may legitimately pass 24.
 * - **`prose`** — `7 h 30`, with {@link NARROW_NBSP} on both sides of the `h`.
 *   A sentence, a band total or a dashboard readout, where `48:35` beside a
 *   column of clock times reads as half past midnight.
 *
 * The `h` is not translated. Both catalogues in this product write a duration
 * that way today; a screen whose translator disagrees takes
 * {@link durationParts} and interpolates its own key instead.
 *
 * **Rounding: truncated toward zero, always.** The server's `workedMin` is an
 * integer, so the rule is invisible there — it exists for the live timers,
 * which are fed `ms / 60_000`: a clock that reads `3 h 12` one second before
 * the twelfth minute is over has claimed a minute nobody worked, and every
 * number on this product is a payroll fact.
 */
export interface FormatDurationOptions {
  readonly form?: 'grid' | 'prose';
  /** Keep the sign of a negative span. Off by default, which clamps it to 0. */
  readonly signed?: boolean;
  /** What an unknown renders as. `null` returns `null` so the caller draws its own. */
  readonly blank?: string | null;
  /** Group the hours in thousands with {@link NARROW_NBSP} — `2 626 h 49`. */
  readonly group?: boolean;
}

/**
 * The two halves of a duration, for a catalogue entry that owns its separator.
 *
 * A **type alias**, not an interface, deliberately: only an alias gets
 * TypeScript's implicit index signature, and without one it cannot be handed
 * to i18next's `t()` as interpolation values.
 */
export type DurationParts = { hours: string; minutes: string };

function groupThousands(value: string): string {
  return value.replace(/\B(?=(\d{3})+(?!\d))/gu, NARROW_NBSP);
}

/**
 * `7:30` / `7 h 30` for 450 minutes.
 *
 * `null` is a real answer, not zero (D-12: money edge cases are explicit
 * nulls) — an unclosed session has *unknown* hours, and printing `0:00`
 * would state something false on a payroll screen.
 */
export function formatDuration(
  minutes: number | null | undefined,
  options?: FormatDurationOptions,
): string | null {
  const blank = options?.blank === undefined ? '—' : options.blank;
  if (minutes === null || minutes === undefined || !Number.isFinite(minutes)) return blank;

  const signed = options?.signed === true;
  const value = signed ? minutes : Math.max(0, minutes);
  const total = Math.trunc(Math.abs(value));
  const sign = value < 0 ? '-' : '';
  const hours = String(Math.floor(total / 60));
  const rest = String(total % 60).padStart(2, '0');
  const head = options?.group === true ? groupThousands(hours) : hours;

  return options?.form === 'prose'
    ? `${sign}${head}${NARROW_NBSP}h${NARROW_NBSP}${rest}`
    : `${sign}${head}:${rest}`;
}

/** `519` becomes `{ hours: '8', minutes: '39' }`; `null` in, `null` out. */
export function durationParts(minutes: number | null | undefined): DurationParts | null {
  const grid = formatDuration(minutes, { blank: null });
  if (grid === null) return null;
  const [hours = '0', rest = '00'] = grid.split(':');
  return { hours, minutes: rest };
}

/**
 * `7:30` for 450 minutes.
 *
 * `null` is a real answer, not zero (D-12: money edge cases are explicit
 * nulls) — an unclosed session has *unknown* hours, and printing `0:00`
 * would state something false on a payroll screen.
 */
export function formatDurationHhMm(minutes: number | null | undefined, blank = '—'): string {
  return formatDuration(minutes, { blank }) ?? blank;
}

/**
 * The `business_date` an instant belongs to (D-07): an overnight shift
 * belongs to its **start** date in the site's zone. A 22:00–06:00 shift that
 * starts on the 7th is the 7th's shift, all eight hours of it.
 */
export function businessDateOf(startIso: string, zone: string): string {
  return DateTime.fromISO(startIso, { zone }).toFormat('yyyy-LL-dd');
}

/**
 * Elapsed minutes between two instants, DST included: 22:00→06:00 across the
 * November fall-back is 540 minutes, not 480.
 */
export function minutesBetween(startIso: string, endIso: string): number {
  const start = DateTime.fromISO(startIso, { zone: 'utc' });
  const end = DateTime.fromISO(endIso, { zone: 'utc' });
  return Math.round(end.diff(start, 'minutes').minutes);
}

/** `true` when the two instants fall on different calendar days in the zone. */
export function crossesMidnight(startIso: string, endIso: string, zone: string): boolean {
  return businessDateOf(startIso, zone) !== businessDateOf(endIso, zone);
}
